/** SUNTRONIX ONE — Employee Offboarding (Subsidiary-first).
 *
 *  Two-panel screen keyed on Master Company + Subsidiary (Company).
 *
 *  1. Master + Subsidiary picker — every filter afterwards is company-scoped
 *     so the operator only sees devices that legally belong to the client.
 *  2. Company employees strip — pick one to open the recovery flow; missing
 *     employees can be created inline before the pickup is scheduled.
 *  3. Company asset ledger — every asset that belongs to the subsidiary,
 *     with its current assignee inline.  Missing assets can be added
 *     with the "+ Add asset" dialog so no offboarding is blocked by a
 *     mid-lifecycle data gap.
 *  4. Recovery drawer — the existing schedule-pickup flow, unchanged.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import NextActionPanel from "@/components/os/NextActionPanel";

const errMsg = (e) => formatApiError(e?.response?.data?.detail);
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import StatusBadge from "@/components/app/StatusBadge";
import AssigneeTypeahead from "@/components/app/AssigneeTypeahead";
import {
  UserMinus, Undo2, UserPlus, PackagePlus, Loader2, Search, Users,
  Building2, ChevronRight, RefreshCw,
} from "lucide-react";


function EmptyState({ icon: Icon, title, hint, action }) {
  return (
    <div className="text-center py-8 px-4">
      {Icon && <Icon className="h-6 w-6 mx-auto text-slate-300 mb-2" />}
      <div className="text-[13px] font-medium text-slate-800">{title}</div>
      {hint && <div className="text-[12px] text-slate-500 mt-0.5">{hint}</div>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}


function CreateEmployeeDialog({ open, onOpenChange, masterCompanyId,
                                        companyId, onCreated, seedEmail }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (open) setForm({
      name: "", email: seedEmail || "", employee_code: "",
      phone: "", department: "", cost_centre: "",
      joining_date: "", manager_email: "", delivery_address: "",
    });
  }, [open, seedEmail]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name?.trim() || !form.email?.trim() || !form.employee_code?.trim()) {
      toast.error("Name, email and employee code are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        master_company_id: masterCompanyId,
        company_id: companyId,
        ...Object.fromEntries(Object.entries(form).map(([k, v]) =>
          [k, typeof v === "string" ? v.trim() || null : v])),
      };
      const { data } = await api.post("/employees", payload);
      toast.success(`Employee ${data.name} created.`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e) || "Could not create employee");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="offboarding-create-employee-dialog"
                          className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add employee</DialogTitle>
          <div className="text-[12px] text-slate-500">
            Missing employees can be created inline so an offboarding
            pickup is never blocked by a data gap.
          </div>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          {[
            ["name", "Full name *", "Priya Sharma"],
            ["email", "Work email *", "priya@client.com"],
            ["employee_code", "Employee code *", "EMP-1042"],
            ["phone", "Phone", ""],
            ["department", "Department", ""],
            ["cost_centre", "Cost centre", ""],
            ["joining_date", "Joined", "YYYY-MM-DD"],
            ["manager_email", "Manager email", ""],
          ].map(([k, label, ph]) => (
            <label key={k} className="text-[12px]">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">
                {label}
              </div>
              <Input value={form[k] || ""}
                onChange={(e) => set(k, e.target.value)}
                placeholder={ph}
                data-testid={`offboarding-emp-${k}`} />
            </label>
          ))}
          <label className="text-[12px] col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">
              Delivery address
            </div>
            <Input value={form.delivery_address || ""}
              onChange={(e) => set("delivery_address", e.target.value)}
              placeholder="Pickup address for offboarding"
              data-testid="offboarding-emp-delivery_address" />
          </label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}
                     disabled={busy}
                     data-testid="offboarding-emp-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy}
                     data-testid="offboarding-emp-save">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> :
              <UserPlus className="h-3.5 w-3.5 mr-1" />}
            Create employee
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function CreateAssetDialog({ open, onOpenChange, masterCompanyId, companyId,
                                    employees, onCreated }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});
  const [assignTo, setAssignTo] = useState("");
  const [assignSearch, setAssignSearch] = useState("");

  useEffect(() => {
    if (open) {
      setForm({
        asset_tag: "", serial: "", brand: "", model: "", category: "Laptop",
        purchase_date: "", warranty_end: "",
      });
      setAssignTo(""); setAssignSearch("");
    }
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.asset_tag?.trim() || !form.serial?.trim()) {
      toast.error("Asset tag and serial are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        master_company_id: masterCompanyId,
        company_id: companyId,
        ownership_type: "customer",
        ...Object.fromEntries(Object.entries(form).map(([k, v]) =>
          [k, typeof v === "string" ? v.trim() || null : v])),
      };
      const { data } = await api.post("/assets/", payload);
      // Optional inline assignment to an employee
      if (assignTo) {
        try {
          await api.patch(`/assets/${data.id}`, {
            current_employee_id: assignTo,
            lifecycle_state: "in_use",
          });
        } catch (e) {
          toast.error("Asset created but assignment failed: "
                          + (errMsg(e) || "unknown"));
        }
      }
      toast.success(`Asset ${data.asset_tag} added.`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e) || "Could not create asset");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="offboarding-create-asset-dialog"
                          className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add asset for this client</DialogTitle>
          <div className="text-[12px] text-slate-500">
            Register a device that isn&apos;t yet in the Golden Asset Record.
            You can optionally assign it to an employee in the same step.
          </div>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          {[
            ["asset_tag", "Asset tag *", "PADA-LT-01042"],
            ["serial", "Serial *", "C02XJ7XYJG5H"],
            ["brand", "Brand", "Apple"],
            ["model", "Model", "MacBook Pro 14"],
            ["category", "Category", "Laptop"],
            ["purchase_date", "Purchase date", "YYYY-MM-DD"],
            ["warranty_end", "Warranty end", "YYYY-MM-DD"],
          ].map(([k, label, ph]) => (
            <label key={k} className="text-[12px]">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">
                {label}
              </div>
              <Input value={form[k] || ""}
                onChange={(e) => set(k, e.target.value)}
                placeholder={ph}
                data-testid={`offboarding-asset-${k}`} />
            </label>
          ))}
          <label className="text-[12px] col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">
              Assign to employee (optional)
            </div>
            <AssigneeTypeahead
              value={assignSearch}
              onChange={(v) => { setAssignSearch(v); if (!v) setAssignTo(""); }}
              onPick={(it) => {
                if (it.kind === "employee") setAssignTo(it.id);
                else setAssignTo("");
              }}
              masterCompanyId={masterCompanyId}
              companyId={companyId}
              testId="offboarding-asset-assign"
              placeholder="Type name · email · code…"
            />
            {assignTo && (
              <div className="text-[10px] text-emerald-700 mt-0.5">
                ✓ Will be assigned on save
              </div>
            )}
          </label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}
                     disabled={busy}
                     data-testid="offboarding-asset-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy}
                     data-testid="offboarding-asset-save">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> :
              <PackagePlus className="h-3.5 w-3.5 mr-1" />}
            Add asset
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function RecoveryDrawer({ employee, assets, open, onOpenChange, masterCompanyId,
                                  onScheduled }) {
  const initialIds = useMemo(() => (assets || []).map((a) => a.id),
                                  [assets]);
  const [assetIds, setAssetIds] = useState(initialIds);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setAssetIds(initialIds); }, [initialIds]);

  const toggle = (id) => setAssetIds((s) => s.includes(id)
    ? s.filter((x) => x !== id) : [...s, id]);

  const schedule = async () => {
    if (!employee) return;
    setBusy(true);
    try {
      await api.post("/reverse-pickups", {
        master_company_id: masterCompanyId,
        employee_id: employee.id,
        asset_ids: assetIds,
        pickup_address: employee.delivery_address || "TBD",
        reason: "employee_offboarding",
      });
      const n = assetIds.length;
      toast.success(n > 0
        ? `Recovery scheduled for ${n} device(s)`
        : "Recovery scheduled — no devices linked yet");
      onScheduled?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e) || "Failed to schedule pickup.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="offboarding-recovery-dialog"
                          className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Recover devices from {employee?.name || "…"}</DialogTitle>
          <div className="text-[12px] text-slate-500">
            {employee?.email} · {employee?.department || "—"}
          </div>
        </DialogHeader>

        <div className="border border-slate-200 rounded-md">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-8"></TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500">Asset</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500">Serial</TableHead>
                <TableHead className="text-[10px] uppercase text-slate-500">State</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(assets || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}
                                  className="text-center py-6 text-sm text-slate-500"
                                  data-testid="offboarding-no-assets">
                    No devices currently assigned. A placeholder pickup will
                    be scheduled; devices recovered on inspection will be
                    linked back here.
                  </TableCell>
                </TableRow>
              ) : (assets || []).map((a) => (
                <TableRow key={a.id} className="hover:bg-slate-50">
                  <TableCell>
                    <input type="checkbox"
                                checked={assetIds.includes(a.id)}
                                onChange={() => toggle(a.id)}
                                data-testid={`offboarding-check-${a.id}`} />
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="font-mono text-[12px]">{a.asset_tag}</div>
                    <div className="text-[11px] text-slate-500">
                      {a.brand} {a.model}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{a.serial}</TableCell>
                  <TableCell><StatusBadge value={a.lifecycle_state} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}
                     disabled={busy}>Cancel</Button>
          <Button className="bg-slate-900 hover:bg-slate-800 text-white"
                     onClick={schedule} disabled={busy}
                     data-testid="offboarding-schedule-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> :
              <Undo2 className="h-3.5 w-3.5 mr-1" />}
            Schedule recovery
            {assetIds.length > 0 && <span className="ml-1.5 text-[10px] opacity-80">· {assetIds.length}</span>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
export default function Offboarding() {
  const openRecoveryId = new URLSearchParams(window.location.search).get("open");
  const [masters, setMasters] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedMaster, setSelectedMaster] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");

  const [employees, setEmployees] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");

  const [createEmpOpen, setCreateEmpOpen] = useState(false);
  const [createAssetOpen, setCreateAssetOpen] = useState(false);
  const [recoveryEmp, setRecoveryEmp] = useState(null);

  // Load master companies once
  useEffect(() => {
    api.get("/master-companies")
      .then(({ data }) => setMasters(data.items || []))
      .catch(() => setMasters([]));
  }, []);

  // When Master picked, load its subsidiaries
  useEffect(() => {
    setSelectedCompany("");
    setEmployees([]); setAssets([]);
    if (!selectedMaster) { setCompanies([]); return; }
    api.get("/companies", { params: { master_company_id: selectedMaster } })
      .then(({ data }) => setCompanies(data.items || []))
      .catch(() => setCompanies([]));
  }, [selectedMaster]);

  const loadCompanyData = useCallback(async () => {
    if (!selectedMaster || !selectedCompany) return;
    setLoading(true);
    try {
      const [emp, ast] = await Promise.all([
        api.get("/employees", { params: {
          master_company_id: selectedMaster,
          company_id: selectedCompany, limit: 500,
        }}),
        api.get("/assets/", { params: {
          master_company_id: selectedMaster,
          company_id: selectedCompany, limit: 200,
        }}),
      ]);
      setEmployees(emp.data.items || []);
      setAssets(ast.data.items || []);
    } catch (e) {
      toast.error(errMsg(e) || "Could not load client data");
    } finally { setLoading(false); }
  }, [selectedMaster, selectedCompany]);

  useEffect(() => { loadCompanyData(); }, [loadCompanyData]);

  const empById = useMemo(() => {
    const m = new Map();
    for (const e of employees) m.set(e.id, e);
    return m;
  }, [employees]);

  const assetsByEmp = useMemo(() => {
    const m = new Map();
    for (const a of assets) {
      const k = a.current_employee_id || "__unassigned__";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(a);
    }
    return m;
  }, [assets]);

  const filteredAssets = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return assets;
    return assets.filter((a) => {
      const emp = a.current_employee_id
        ? empById.get(a.current_employee_id) : null;
      const hay = [
        a.asset_tag, a.serial, a.brand, a.model, a.lifecycle_state,
        emp?.name, emp?.email, emp?.department,
      ].map((x) => (x || "").toString().toLowerCase()).join(" ");
      return hay.includes(q);
    });
  }, [assets, filter, empById]);

  const selectedMasterName = masters.find((m) => m.id === selectedMaster)?.name;
  const selectedCompanyName = companies.find((c) => c.id === selectedCompany)?.name;

  const openRecovery = (emp) => setRecoveryEmp(emp);
  const empAssets = (empId) => assetsByEmp.get(empId) || [];

  return (
    <div className="space-y-4 max-w-[1400px]" data-testid="offboarding-page">
      {openRecoveryId && <NextActionPanel kind="recovery" id={openRecoveryId} />}
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Lifecycle</div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Employee Offboarding</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Recover devices for an exiting employee.  Pick the client, add
          any missing employee or asset records inline, then schedule the
          pickup.
        </p>
      </div>

      {/* Master + Subsidiary picker */}
      <div className="bg-white border border-slate-200 rounded-md p-5 grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
            <Building2 className="h-3 w-3" /> Master Company
          </label>
          <Select value={selectedMaster} onValueChange={setSelectedMaster}>
            <SelectTrigger className="h-10 mt-1" data-testid="offboarding-master-select">
              <SelectValue placeholder="Select master…" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {masters.map((m) => (
                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
            <Building2 className="h-3 w-3" /> Client (Subsidiary Company)
          </label>
          <Select value={selectedCompany} onValueChange={setSelectedCompany}
                     disabled={!selectedMaster}>
            <SelectTrigger className="h-10 mt-1"
                                data-testid="offboarding-company-select">
              <SelectValue placeholder={selectedMaster
                ? (companies.length ? "Select client…" : "No clients under this master")
                : "Pick a master first"} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {selectedCompany && (
        <>
          {/* Client header + inline create actions */}
          <div className="bg-white border border-slate-200 rounded-md px-4 py-3 flex flex-wrap items-center gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">
                Client
              </div>
              <div className="text-[14px] font-semibold text-slate-900 truncate">
                {selectedMasterName} · {selectedCompanyName}
              </div>
              <div className="text-[11px] text-slate-500">
                {employees.length} employee{employees.length === 1 ? "" : "s"}
                {" · "}
                {assets.length} asset{assets.length === 1 ? "" : "s"} on file
              </div>
            </div>
            <Button variant="outline" onClick={loadCompanyData}
                       disabled={loading}
                       data-testid="offboarding-refresh">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button variant="outline"
                       onClick={() => setCreateEmpOpen(true)}
                       data-testid="offboarding-add-employee">
              <UserPlus className="h-3.5 w-3.5 mr-1" /> Add employee
            </Button>
            <Button className="bg-slate-900 hover:bg-slate-800 text-white"
                       onClick={() => setCreateAssetOpen(true)}
                       data-testid="offboarding-add-asset">
              <PackagePlus className="h-3.5 w-3.5 mr-1" /> Add asset
            </Button>
          </div>

          {/* Employees strip */}
          <div className="bg-white border border-slate-200 rounded-md">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="text-sm font-heading font-semibold text-slate-900 flex items-center gap-1">
                <Users className="h-3.5 w-3.5" /> Employees
              </div>
              <div className="text-[11px] text-slate-500">
                {employees.length} on file
              </div>
            </div>
            {employees.length === 0 ? (
              <EmptyState icon={Users}
                              title="No employees on file for this client"
                              hint="Add the exiting employee inline before scheduling the pickup."
                              action={
                <Button variant="outline"
                             onClick={() => setCreateEmpOpen(true)}
                             data-testid="offboarding-empty-add-employee">
                  <UserPlus className="h-3.5 w-3.5 mr-1" /> Add employee
                </Button>
              } />
            ) : (
              <div className="divide-y divide-slate-100 max-h-[280px] overflow-y-auto">
                {employees.map((e) => {
                  const nA = empAssets(e.id).length;
                  return (
                    <button key={e.id} type="button"
                                 onClick={() => openRecovery(e)}
                                 data-testid={`offboarding-emp-row-${e.id}`}
                                 className="w-full text-left px-4 py-2.5 flex items-center gap-2 hover:bg-slate-50 transition">
                      <div className="h-7 w-7 rounded-full bg-slate-100 grid place-items-center text-[11px] font-semibold text-slate-600">
                        {(e.name || "?").slice(0, 1).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-medium text-slate-900 truncate">
                          {e.name}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {e.email} · {e.department || "—"}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">
                          Assets
                        </div>
                        <div className="text-[13px] font-semibold tabular-nums">
                          {nA}
                        </div>
                      </div>
                      <span className="ml-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-slate-500">
                        <UserMinus className="h-3 w-3" /> Recover
                      </span>
                      <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Company asset ledger */}
          <div className="bg-white border border-slate-200 rounded-md">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
              <div className="text-sm font-heading font-semibold text-slate-900">
                Assets on file
              </div>
              <div className="relative w-64">
                <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
                <Input value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filter tag · serial · employee…"
                  className="pl-8 h-9 text-[13px]"
                  data-testid="offboarding-asset-filter" />
              </div>
            </div>
            {loading && assets.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-[12px]">
                <Loader2 className="h-4 w-4 mx-auto animate-spin mb-2" /> Loading…
              </div>
            ) : assets.length === 0 ? (
              <EmptyState icon={PackagePlus}
                              title="No assets registered for this client yet"
                              hint="Add the missing devices before scheduling recovery so the pickup reflects the full inventory."
                              action={
                <Button variant="outline"
                             onClick={() => setCreateAssetOpen(true)}
                             data-testid="offboarding-empty-add-asset">
                  <PackagePlus className="h-3.5 w-3.5 mr-1" /> Add asset
                </Button>
              } />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-[10px] uppercase text-slate-500">Asset</TableHead>
                      <TableHead className="text-[10px] uppercase text-slate-500">Serial</TableHead>
                      <TableHead className="text-[10px] uppercase text-slate-500">Assignee</TableHead>
                      <TableHead className="text-[10px] uppercase text-slate-500">State</TableHead>
                      <TableHead className="text-[10px] uppercase text-slate-500 text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAssets.map((a) => {
                      const emp = a.current_employee_id
                        ? empById.get(a.current_employee_id) : null;
                      return (
                        <TableRow key={a.id} className="hover:bg-slate-50"
                                        data-testid={`offboarding-asset-row-${a.id}`}>
                          <TableCell className="text-sm">
                            <div className="font-mono text-[12px]">{a.asset_tag}</div>
                            <div className="text-[11px] text-slate-500">
                              {a.brand || "—"} {a.model || ""}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-[11px]">{a.serial || "—"}</TableCell>
                          <TableCell>
                            {emp ? (
                              <>
                                <div className="text-[12px]">{emp.name}</div>
                                <div className="text-[10px] text-slate-500">{emp.email}</div>
                              </>
                            ) : a.current_employee_id ? (
                              <span className="text-[11px] text-amber-700 font-medium"
                                     data-testid={`offboarding-asset-orphan-${a.id}`}>
                                Assigned to missing employee
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setCreateEmpOpen(true)}
                                data-testid={`offboarding-asset-create-emp-${a.id}`}
                                className="text-[11px] text-slate-600 hover:text-slate-900 underline underline-offset-2">
                                Unassigned · Create employee
                              </button>
                            )}
                          </TableCell>
                          <TableCell><StatusBadge value={a.lifecycle_state} /></TableCell>
                          <TableCell className="text-right">
                            {emp && (
                              <Button size="sm" variant="ghost"
                                          onClick={() => openRecovery(emp)}
                                          data-testid={`offboarding-asset-recover-${a.id}`}>
                                Recover
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {filteredAssets.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}
                                        className="text-center py-6 text-sm text-slate-500">
                          No assets match your filter.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </>
      )}

      <CreateEmployeeDialog
        open={createEmpOpen}
        onOpenChange={setCreateEmpOpen}
        masterCompanyId={selectedMaster}
        companyId={selectedCompany}
        onCreated={loadCompanyData}
      />

      <CreateAssetDialog
        open={createAssetOpen}
        onOpenChange={setCreateAssetOpen}
        masterCompanyId={selectedMaster}
        companyId={selectedCompany}
        employees={employees}
        onCreated={loadCompanyData}
      />

      <RecoveryDrawer
        open={!!recoveryEmp}
        onOpenChange={(v) => { if (!v) setRecoveryEmp(null); }}
        employee={recoveryEmp}
        assets={recoveryEmp ? empAssets(recoveryEmp.id) : []}
        masterCompanyId={selectedMaster}
        onScheduled={loadCompanyData}
      />
    </div>
  );
}
